import React from "react";
import { useState, useEffect } from "react";
import { MatchSetupConfig } from "../../models/match";
import { PlayerRecord } from "../PlayerRecord";
import { showDateTime } from "../../utils/dateUtils";
import { TeamHeader } from "../teams/TeamHeader";
import { PublicTeam } from "../../models/groupedTeams";

const formatFlightDataForCSV = (
  groupedFlights: Record<string, any[] | undefined>,
  players: any[] | null,
  showMarkers: boolean,
  teams: any[],
  time?: string,
  isDoubles?: boolean,
  isMatchPlay?: boolean,
  isSerialTeeOff?: boolean
) => {
  const rows: any[] = [];
  if (!groupedFlights) {
    return rows;
  }

  Object.entries(groupedFlights).forEach(([holeDescription, flights]) => {
    if (!flights) return;
    const hole = flights[0].holeNumber;
    const row: any = {
      hole: isSerialTeeOff ?  `${hole}` : holeDescription,
      dateTime:
        flights[0]?.dateTime?.length === 8
          ? showDateTime(`${flights[0].dateTime}${time}`)
          : showDateTime(flights[0].dateTime),
    };

    // Process each flight in the hole
    flights.forEach((flight, index) => {
      const player = players?.find((p) => p.playerId === flight.playerId);
      const player2 = isDoubles
        ? players?.find((p) => p.playerId === flight.player2Id)
        : null;
      const marker = players?.find((p) => p.playerId === flight.markPlayerId);
      const marker2 = isDoubles
        ? players?.find((p) => p.playerId === flight.markPlayer2Id)
        : null;
      const team = teams.find((t) => t.id === flight.teamId);

      row[`team${index + 1}`] = team?.name || "";
      row[`players${index + 1}`] = `${player?.firstName || ""} ${
        player?.lastName || ""
      }${
        isDoubles && player2
          ? ` / ${player2.firstName || ""} ${player2.lastName || ""}`
          : ""
      }`.trim();

      if (showMarkers && !isMatchPlay) {
        row[`markers${index + 1}`] = `${marker?.firstName || ""} ${
          marker?.lastName || ""
        }${
          isDoubles && marker2
            ? ` / ${marker2.firstName || ""} ${marker2.lastName || ""}`
            : ""
        }`.trim();
      }
    });

    rows.push(row);
  });
  return rows.sort((a, b) => a.hole.localeCompare(b.hole, undefined, { numeric: true }));
};

interface FlightDetailsViewProps {
  players: any[] | null;
  teams: PublicTeam[] | null;
  setupConfig: MatchSetupConfig;
}

export const FlightDetailsView: React.FC<FlightDetailsViewProps> = ({
  setupConfig,
  players,
  teams,
}) => {
  const [groupedFlights, setGroupedFlights] = useState<
    Record<string, typeof setupConfig.flights>
  >({});

  const [showMarkers, setShowMarkers] = useState(false);

  useEffect(() => {
    const grouped = setupConfig.flights?.reduce((acc, flight) => {
      if (!acc[flight.holeDescription]) {
        acc[flight.holeDescription] = [];
      }
      acc[flight.holeDescription].push(flight);
      return acc;
    }, {} as Record<string, typeof setupConfig.flights>);
    setGroupedFlights(grouped || {});
  }, [setupConfig, setupConfig.flights]);

  const getTeamById = (teamId: string) => {
    return teams?.find((t) => t.id === teamId);
  };

  const isDoubles = setupConfig.playFormat === "doubles";
  const isMatchPlay = setupConfig.gameFormat === "matchplay";
  const isSerialTeeOff = setupConfig.teeOffType === "serial";
  const handleDownloadCSV = () => {
    const data = formatFlightDataForCSV(
      groupedFlights,
      players,
      showMarkers,
      teams || [],
      setupConfig.time,
      isDoubles,
      isMatchPlay,
      isSerialTeeOff
    );

    // Find max number of flights per hole to determine number of columns
    const maxFlights = Math.max(
      ...Object.values(groupedFlights).map((flights) => flights?.length || 0)
    );

    // Create headers based on number of flights
    const headers = ["Hole", "Date Time"];
    for (let i = 1; i <= maxFlights; i++) {
      headers.push(`Team ${i}`);
      headers.push(`Players ${i}`);
      if (showMarkers) {
        headers.push(`Markers ${i}`);
      }
    }

    const csvContent = [
      headers.join(","),
      ...data.map((row) => {
        const rowData = [row.hole, row.dateTime];
        for (let i = 1; i <= maxFlights; i++) {
          rowData.push(
            `"${row[`team${i}`] || ""}"`,
            `"${row[`players${i}`] || ""}"`,
            ...(showMarkers ? [`"${row[`markers${i}`] || ""}"`] : [])
          );
        }
        return rowData.join(",");
      }),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `flights_${new Date().toISOString().split("T")[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const getHandicap = (flight: any) => {
    if (isDoubles) {
      const h1 = flight.playerHandicap;
      const h2 = flight.player2Handicap;
      const smallerHandicap = Math.min(h1, h2);
      const largerHandicap = Math.max(h1, h2);
      const teamHandicap = 0.6 * smallerHandicap + 0.4 * largerHandicap;
      return Math.round(teamHandicap);
    }
    return flight.playerHandicap;
  };

  const renderPlayerDetails = (flight: any) => {
    const hc = getHandicap(flight);

    if (isDoubles) {
      return (
        <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-4">
          <div className="flex flex-col space-y-2">
            <PlayerRecord
              member={players?.find((p) => p.playerId === flight.playerId)}
              showHandicapAndMsl={false}
            />
            <PlayerRecord
              member={players?.find((p) => p.playerId === flight.player2Id)}
              showHandicapAndMsl={false}
            />
          </div>
          {hc && (
            <div className="flex flex-col justify-center">
              <span className="text-sm sm:text-base text-gray-700 dark:text-gray-300">
                Team Handicap: {hc}
              </span>
            </div>
          )}
        </div>
      );
    }

    return (
      <div className="flex items-center space-x-4">
        <PlayerRecord
          member={players?.find((p) => p.playerId === flight.playerId)}
          showHandicapAndMsl={false}
        />
        {hc && (
          <span className="text-sm sm:text-base text-gray-700 dark:text-gray-300">
            HC: {hc}
          </span>
        )}
      </div>
    );
  };

  const renderMarkerDetails = (flight: any) => {
    if (!showMarkers) return null;

    return (
      <div className="flex flex-col sm:flex-row items-start sm:items-center mt-2 pt-2 border-t border-gray-200 dark:border-gray-600">
        <span className="text-sm sm:text-base font-medium text-gray-700 dark:text-gray-300 mb-1 sm:mb-0 sm:mr-2">
          Will mark scores for
        </span>
        <div className="flex flex-col space-y-2">
          <PlayerRecord
            member={players?.find((p) => p.playerId === flight.markPlayerId)}
            showHandicapAndMsl={false}
          />
          {isDoubles && flight.markPlayer2Id && (
            <PlayerRecord
              member={players?.find((p) => p.playerId === flight.markPlayer2Id)}
              showHandicapAndMsl={false}
            />
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="container mx-auto sm:px-4">
      {Object.keys(groupedFlights).length > 0 && (
        <div className="flex items-center justify-end mt-2 mb-1 sm:mt-0 gap-4">
          {!isMatchPlay && (
            <div className="flex items-center">
              <label className="mr-2 text-sm sm:text-base text-gray-300">
                Show markers
              </label>
              <input
                type="checkbox"
                className="form-checkbox h-4 w-4 sm:h-5 sm:w-5 text-blue-600"
                onChange={(e) => setShowMarkers(e.target.checked)}
              />
            </div>
          )}
          {isMatchPlay && (
            <span className="text-white">Players mark for all players in flight.</span>
          )}
          <button
            onClick={handleDownloadCSV}
            className="px-3 py-1 text-sm sm:text-base bg-blue-600 text-white rounded-sm hover:bg-blue-700 transition-colors"
          >
            Download flights
          </button>
        </div>
      )}
      {Object.entries(groupedFlights).map(([holeDescription, flights]) => (
        <div key={holeDescription} className="mb-4 sm:mb-8">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-3 sm:p-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4">
              <div>
                <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white">
                  Hole {holeDescription}: {flights && flights.length} flights
                </h3>
                {flights && flights.length > 0 && (
                  <p className="text-sm sm:text-base text-gray-600 dark:text-gray-300 mt-1">
                    {showDateTime(
                      flights[0].dateTime.length === 8
                        ? `${flights[0].dateTime}${setupConfig.time}`
                        : flights[0].dateTime
                    )}
                  </p>
                )}
              </div>
            </div>

            <div className="space-y-3 sm:space-y-4">
              {flights &&
                flights.map((flight, index) => (
                  <div
                    key={index}
                    className="p-3 sm:p-4 bg-gray-50 dark:bg-gray-700 rounded-lg shadow-sm"
                  >
                    <div className="flex flex-col sm:grid sm:grid-cols-2 gap-2 sm:gap-4">
                      <div className="flex items-center">
                        <span className="font-medium text-gray-700 dark:text-gray-300 mr-2 text-sm sm:text-base">
                          {index + 1}.
                        </span>
                        <TeamHeader
                          team={getTeamById(flight.teamId)}
                          hideAvatar
                        />
                      </div>
                      {renderPlayerDetails(flight)}
                    </div>
                    {renderMarkerDetails(flight)}
                  </div>
                ))}
            </div>
          </div>
        </div>
      ))}

      {Object.keys(groupedFlights).length === 0 && (
        <div className="text-center text-gray-700 dark:text-gray-300 mt-4">
          No flights available for this match.
        </div>
      )}
    </div>
  );
};
